import { useDeferredValue, useState } from 'react';
import { message, notification } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '~/app/auth/context/AuthContext';
import type { PolygonPerimeter } from '@evimo/types';
import {
  SiteLocationSummary,
  SiteService,
  type CityLookup,
  type PaginationMeta,
  type ProvinceLookup,
  type SiteLookup,
  type UpdateSitePayload,
} from '~/app/services/site/site.service';
import type { AddressAutocompleteSelection } from '~/app/hooks/site/useSiteAddressAutocomplete';

export type SiteFormValues = {
  name: string;
  address?: string;
  postalCode?: string;
  cityCode?: string;
  provinceCode?: string;
  latitude?: string;
  longitude?: string;
  perimeter?: PolygonPerimeter | null;
};

const SITE_PAGE_SIZE = 10;

function toNullableString(value?: string): string | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  const normalizedValue = value.trim();

  return normalizedValue ? normalizedValue : null;
}

function toNullableNumber(value?: string): number | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return null;
  }

  return Number(normalizedValue);
}

function normalizeLookupLabel(value?: string): string {
  return (
    value
      ?.normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .trim()
      .toLowerCase() ?? ''
  );
}

export function useSiteManagement(selectedProvinceCode?: string) {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';

  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [editingSite, setEditingSite] = useState<SiteLookup | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [provinceSearchValue, setProvinceSearchValue] = useState('');
  const [citySearchValue, setCitySearchValue] = useState('');
  const deferredSearchValue = useDeferredValue(searchValue.trim());
  const deferredProvinceSearchValue = useDeferredValue(
    provinceSearchValue.trim(),
  );
  const deferredCitySearchValue = useDeferredValue(citySearchValue.trim());
  const siteQueryKey = [
    'sites',
    organizationId,
    page,
    SITE_PAGE_SIZE,
    deferredSearchValue,
  ] as const;

  const query = useQuery({
    queryKey: siteQueryKey,
    queryFn: () =>
      SiteService.listByOrganizationId(organizationId, {
        page,
        limit: SITE_PAGE_SIZE,
        q: deferredSearchValue || undefined,
      }),
    enabled: !!organizationId,
  });

  const provincesQuery = useQuery({
    queryKey: ['site-provinces', deferredProvinceSearchValue],
    queryFn: () =>
      SiteService.listProvinces({
        page: 1,
        limit: 50,
        q: deferredProvinceSearchValue || undefined,
      }),
    enabled: isModalOpen,
  });

  const citiesQuery = useQuery({
    queryKey: [
      'site-cities',
      deferredCitySearchValue,
      selectedProvinceCode ?? '',
    ],
    queryFn: () =>
      SiteService.listCities({
        page: 1,
        limit: 50,
        q: deferredCitySearchValue || undefined,
        provinceCode: selectedProvinceCode || undefined,
      }),
    enabled: isModalOpen,
  });

  const createMutation = useMutation({
    mutationFn: (values: SiteFormValues) =>
      SiteService.create({
        organizationId,
        name: values.name.trim(),
        address: toNullableString(values.address),
        postalCode: toNullableString(values.postalCode),
        cityCode: toNullableString(values.cityCode),
        provinceCode: toNullableString(values.provinceCode),
        latitude: toNullableNumber(values.latitude),
        longitude: toNullableNumber(values.longitude),
        perimeter: values.perimeter ?? null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      notification.success({
        message: 'Site created successfully',
        description:
          'The site is now available in locations management and ready for zone and asset assignment.',
        placement: 'topRight',
        duration: 4.5,
      });
      setIsModalOpen(false);
      setEditingSite(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      siteId,
      values,
    }: {
      siteId: string;
      values: SiteFormValues;
    }) => {
      const payload: UpdateSitePayload = {
        name: values.name.trim(),
        address: toNullableString(values.address),
        postalCode: toNullableString(values.postalCode),
        cityCode: toNullableString(values.cityCode),
        provinceCode: toNullableString(values.provinceCode),
        latitude: toNullableNumber(values.latitude),
        longitude: toNullableNumber(values.longitude),
        perimeter: values.perimeter ?? null,
      };

      return SiteService.updateById(organizationId, siteId, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      notification.success({
        message: 'Site updated successfully',
        description:
          'The site details were saved and the latest map and location data are now active.',
        placement: 'topRight',
        duration: 4.5,
      });
      setIsModalOpen(false);
      setEditingSite(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (siteId: string) =>
      SiteService.deleteById(organizationId, siteId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      await queryClient.invalidateQueries({ queryKey: ['zones'] });
      message.success('Site deleted successfully.');
    },
  });

  const sites = query.data?.data ?? [];
  const pagination =
    query.data?.meta ??
    ({
      total: 0,
      page,
      limit: SITE_PAGE_SIZE,
      totalPages: 0,
    } satisfies PaginationMeta);
  const provinces = provincesQuery.data?.data ?? [];
  const cities = citiesQuery.data?.data ?? [];

  const openCreateModal = (): void => {
    setEditingSite(null);
    setProvinceSearchValue('');
    setCitySearchValue('');
    setIsModalOpen(true);
  };

  const openEditModal = (site: SiteLookup): void => {
    setEditingSite(site);
    setProvinceSearchValue(site.province?.name ?? '');
    setCitySearchValue(site.city?.name ?? '');
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
    setEditingSite(null);
    setProvinceSearchValue('');
    setCitySearchValue('');
  };

  const submitSite = async (values: SiteFormValues): Promise<void> => {
    if (editingSite) {
      await updateMutation.mutateAsync({ siteId: editingSite.id, values });
      return;
    }

    await createMutation.mutateAsync(values);
  };

  const deleteSite = async (siteId: string): Promise<void> => {
    await deleteMutation.mutateAsync(siteId);
  };

  const updateSearchValue = (value: string): void => {
    setSearchValue(value);
    setPage(1);
  };

  const updatePage = (nextPage: number): void => {
    setPage(nextPage);
  };

  const findProvinceByCode = (
    provinceCode?: string,
  ): ProvinceLookup | undefined =>
    provinceCode
      ? (provinces.find((province) => province.code === provinceCode) ??
        (editingSite?.province?.code === provinceCode
          ? {
              code: editingSite.province.code,
              name: editingSite.province.name,
            }
          : undefined))
      : undefined;

  const findCityByCode = (cityCode?: string): CityLookup | undefined => {
    if (!cityCode) {
      return undefined;
    }

    const existingCity = cities.find((city) => city.code === cityCode);

    if (existingCity) {
      return existingCity;
    }

    if (editingSite?.city?.code !== cityCode || !editingSite.province?.code) {
      return undefined;
    }

    return {
      code: editingSite.city.code,
      name: editingSite.city.name,
      provinceCode: editingSite.province.code,
    };
  };

  const resolveAddressSelection = async (
    selection: AddressAutocompleteSelection,
  ) => {
    const normalizedProvinceName = normalizeLookupLabel(selection.provinceName);
    const normalizedCityName = normalizeLookupLabel(selection.cityName);

    const matchingProvince = normalizedProvinceName
      ? (
          await SiteService.listProvinces({
            page: 1,
            limit: 50,
            q: selection.provinceName,
          })
        ).data.find(
          (province) =>
            normalizeLookupLabel(province.name) === normalizedProvinceName,
        )
      : undefined;

    const matchingCity = normalizedCityName
      ? (
          await SiteService.listCities({
            page: 1,
            limit: 50,
            q: selection.cityName,
            provinceCode: matchingProvince?.code,
          })
        ).data.find(
          (city) => normalizeLookupLabel(city.name) === normalizedCityName,
        )
      : undefined;

    return {
      cityCode: matchingCity?.code,
      cityName: matchingCity?.name,
      provinceCode: matchingProvince?.code ?? matchingCity?.provinceCode,
      provinceName:
        matchingProvince?.name ??
        (matchingCity?.provinceCode
          ? provinces.find(
              (province) => province.code === matchingCity.provinceCode,
            )?.name
          : undefined),
    };
  };

  return {
    cities,
    editingSite,
    error: query.error,
    findCityByCode,
    findProvinceByCode,
    isDeleting: deleteMutation.isPending,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isModalOpen,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    page,
    pagination,
    provinces,
    searchValue,
    citySearchValue,
    provinceSearchValue,
    closeModal,
    deleteSite,
    openCreateModal,
    openEditModal,
    refetch: query.refetch,
    resolveAddressSelection,
    setCitySearchValue,
    setProvinceSearchValue,
    submitSite,
    sites,
    updatePage,
    updateSearchValue,
  } satisfies {
    cities: CityLookup[];
    editingSite: SiteLookup | null;
    error: Error | null;
    findCityByCode: (
      cityCode?: string,
    ) => CityLookup | SiteLocationSummary | undefined;
    findProvinceByCode: (
      provinceCode?: string,
    ) => ProvinceLookup | SiteLocationSummary | undefined;
    isDeleting: boolean;
    isFetching: boolean;
    isLoading: boolean;
    isModalOpen: boolean;
    isSubmitting: boolean;
    page: number;
    pagination: PaginationMeta;
    provinces: ProvinceLookup[];
    searchValue: string;
    citySearchValue: string;
    provinceSearchValue: string;
    closeModal: () => void;
    deleteSite: (siteId: string) => Promise<void>;
    openCreateModal: () => void;
    openEditModal: (site: SiteLookup) => void;
    refetch: () => Promise<unknown>;
    resolveAddressSelection: (
      selection: AddressAutocompleteSelection,
    ) => Promise<{
      cityCode?: string;
      cityName?: string;
      provinceCode?: string;
      provinceName?: string;
    }>;
    setCitySearchValue: (value: string) => void;
    setProvinceSearchValue: (value: string) => void;
    submitSite: (values: SiteFormValues) => Promise<void>;
    sites: SiteLookup[];
    updatePage: (page: number) => void;
    updateSearchValue: (value: string) => void;
  };
}
